// src/components/auth/AuthGuard.jsx
import React, { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import LoginForm from './LoginForm';
import AdminSetup from './AdminSetup';
import { FiShield, FiUser } from 'react-icons/fi';
import sessionManager from '../../services/sessionManager';

const AuthGuard = ({ children, requiredRole = null }) => {
  const { currentUser, userProfile, loading, isFirebaseAvailable } = useAuth();
  const { hasPermission } = useTenant();
  const [showLogin, setShowLogin] = React.useState(false);

  // Enhanced session validation
  useEffect(() => {
    if (currentUser && userProfile) {
      // Validate session state and restart idle timeout if needed
      const deviceType = sessionManager.getCurrentDeviceType();
      if (!deviceType) {
        const detectedDeviceType = sessionManager.getDeviceType(userProfile.role);
        sessionManager.setCurrentDeviceType(detectedDeviceType);
        sessionManager.startIdleTimeout(detectedDeviceType, userProfile.role);
      }
    }
  }, [currentUser, userProfile]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If Firebase is not available, show fallback message
  if (!isFirebaseAvailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <FiShield className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Development Mode
            </h2>
            <p className="text-gray-600 mb-6">
              Firebase authentication is not configured. The app is running in development mode with local data only.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>To enable authentication:</strong><br />
                1. Configure Firebase in .env.local<br />
                2. Restart the development server<br />
                3. Deploy to Firebase Hosting for production
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no user is authenticated, show login requirement
  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <FiUser className="mx-auto h-16 w-16 text-blue-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in to access HealeHub. All clinic data and features require authentication for security and compliance.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setShowLogin(true)}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sign In
              </button>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Security Notice:</strong> This system processes healthcare data and requires authentication for GDPR/PDPA compliance.
              </p>
            </div>
          </div>
        </div>

        {showLogin && (
          <LoginForm onClose={() => setShowLogin(false)} />
        )}
      </div>
    );
  }

  // Check role-based permissions if required
  if (requiredRole && !hasPermission(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <FiShield className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this section.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Required Role:</strong> {requiredRole}<br />
                <strong>Your Role:</strong> {userProfile?.role || 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show consent reminder if not given
  const needsConsent = !userProfile?.gdprConsent?.given || !userProfile?.pdpaConsent?.given;
  
  if (needsConsent) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Consent Banner */}
        <div className="bg-yellow-50 border-b border-yellow-200 p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <FiShield className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800">
                  <strong>Data Processing Consent Required:</strong> Please review and provide consent for data processing in your profile settings to ensure GDPR/PDPA compliance.
                </p>
              </div>
            </div>
          </div>
        </div>
        {children}
      </div>
    );
  }

  // User is authenticated and has proper permissions
  return children;
};

export default AuthGuard;
